import { Check, ChevronsUpDown } from "lucide-react";
import { useState } from "react";

import { Icon } from "@/client";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTranslation } from "react-i18next";

type IconSelectorProps = {
  onSelect: (value: Icon) => void;
  value: string;
  disabled?: boolean;
};

const icons = Object.values(Icon).map((icon) => ({
  value: icon,
  label: icon,
}));

function IconSelector(props: IconSelectorProps) {
  const { t } = useTranslation();
  const { onSelect, value, disabled } = props;
  const [open, setOpen] = useState(false);

  const [filteredOptions, setFilteredOptions] = useState(icons);
  const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: filteredOptions.length,
    getScrollElement: () => scrollRef,
    estimateSize: () => 36,
    overscan: 5,
  });

  const virtualOptions = virtualizer.getVirtualItems();

  const handleSearch = (search: string) => {
    if (!search) {
      setFilteredOptions(icons);
      return;
    }

    setFilteredOptions(
      icons.filter((icon) =>
        icon.value.toLowerCase().includes(search.toLowerCase() ?? []),
      ),
    );
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          className="h-8 w-full justify-between"
          disabled={disabled}
        >
          {value ? (
            <span className="flex items-center">
              <span className="material-icons mr-1.5 text-foreground">
                {value.replaceAll("-", "_")}
              </span>
              {icons.find((framework) => framework.value === value)?.label}
            </span>
          ) : (
            t("navItem.selectIcon")
          )}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" side="bottom" align="start">
        <Command shouldFilter={false} onKeyDown={handleKeyDown}>
          <CommandInput
            placeholder={t("navItem.searchIcon")}
            onValueChange={handleSearch}
          />
          <CommandEmpty>{t("navItem.noIcon")}</CommandEmpty>
          <CommandList className="max-h-[500px]">
            <CommandGroup
              ref={setScrollRef}
              style={{
                height: 500,
                width: "100%",
                overflow: "auto",
              }}
            >
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualOptions.map((virtualOption) => (
                  <CommandItem
                    key={filteredOptions[virtualOption.index].value}
                    value={filteredOptions[virtualOption.index].value}
                    onSelect={() => {
                      onSelect(filteredOptions[virtualOption.index].value);
                      setOpen(false);
                    }}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${virtualOption.size}px`,
                      transform: `translateY(${virtualOption.start}px)`,
                    }}
                  >
                    <span className="material-icons mr-1.5 text-foreground">
                      {filteredOptions[virtualOption.index].value.replaceAll(
                        "-",
                        "_",
                      )}
                    </span>
                    {filteredOptions[virtualOption.index].label}
                    <Check
                      className={cn(
                        "ml-auto size-4",
                        value === filteredOptions[virtualOption.index].value
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
              </div>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export { IconSelector };
